import React, { useState } from 'react';
import { ChevronUp, ChevronDown, Eye, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import useOrderBookData from '../hooks/useOrderBookData';
import { formatPriceWithAssetDecimals } from '../utils/priceFormatter';
import { useOrderContext } from '../contexts/OrderContext';
import { useTheme } from '../contexts/ThemeContext';
import Loader from './Loader';

interface MarketDepthProps {
  selectedSymbol: string;
}

const MarketDepth: React.FC<MarketDepthProps> = ({ selectedSymbol }) => {
  const [isMarketDepthExpanded, setIsMarketDepthExpanded] = useState(true);
  const { setSelectedPrice } = useOrderContext();
  const { theme } = useTheme();
  // Use real-time market depth data
  const {
    sells,
    buys,
    maxVolume,
    isLoading,
    error,
    isConnected,
    refreshData,
    reconnect
  } = useOrderBookData(selectedSymbol);

  const handlePriceClick = (price: number) => {
    setSelectedPrice(price);

  };

  return (
    <div className={`card min-h-[50px] flex flex-col p-3 pe-0 mb-0 overflow-hidden w-full md:w-auto relative ${theme === 'light' ? 'border-gray-200' : 'border-gray-800'}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xs font-semibold theme-text-primary p-2 py-0">Market Depth</h3>
        <div className="flex items-center space-x-2 me-3">
          {/* Connection Status */}
          <div className="flex items-center space-x-1">
            {isConnected ? (
              <Wifi className="h-3 w-3 text-[#2AB7A6]" />
            ) : (
              <WifiOff className="h-3 w-3 text-red-400" />
            )}
          </div>
          
          {/* Refresh Button */}
          <button
            onClick={refreshData}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-200 transition-colors duration-200 disabled:opacity-50 hidden"
            title="Refresh data"
          >
            <RefreshCw className={`h-3 w-3 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
          
          {/* Expand/Collapse Button */}
          <button
            onClick={() => setIsMarketDepthExpanded(!isMarketDepthExpanded)}
            className="text-gray-400 hover:text-gray-200 transition-colors duration-200"
          >
            {isMarketDepthExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>

      {isMarketDepthExpanded && (
        <div className="flex flex-col flex-1 overflow-hidden md:h-auto relative">
          {/* Always render loader but control visibility */}
          <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 z-10 ${isLoading ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            {isLoading && (
              <Loader 
                scale='0.5x'
                size="sm" 
                title="" 
                subtitle="" 
              />
            )}
          </div>

          {/* Content - always rendered but hidden when loading */}
          <div className={`flex-1 flex flex-col transition-opacity duration-500 ${isLoading ? 'opacity-0' : 'opacity-100'}`}>
            {/* Error State */}
            {error && (
              <div className="flex-1 flex items-center justify-center">
                <div className="theme-bg-quaternary rounded-lg p-3 py-2 mx-1 text-red-400 w-full">
                  <div className="flex items-center justify-between">
                    <span className="text-xs">{error}</span>
                    <button
                      onClick={reconnect}
                      className="text-xs text-blue-400 hover:text-blue-300"
                    >
                      Reconnect
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Market Depth Visualization */}
            {!isLoading && !error && (
              <>
                <div className="space-y-1 overflow-y-auto flex-1 max-h-full md:max-h-full">
                  {/* Sell Side (Asks) */}
                  <div className="p-0 m-0 w-full">
                    <div className="space-y-1 w-full">
                      {sells.slice(0, 1).map((item: any, index: number) => (
                        <div key={`sell-depth-${index}`} className="flex items-center space-x-2 cursor-pointer hover:theme-bg-quaternary rounded px-2 py-1 transition-colors duration-200 text-gray-400 theme-text-secondary" onClick={() => handlePriceClick(item.price)}>
                          <div className="w-16 text-[11px]">
                            {formatPriceWithAssetDecimals(item.price, selectedSymbol).replace(/\.(\d{1})$/, '.$10')}
                          </div>
                          <div className="flex-1 relative">
                            <div className="h-2 bg-red-500/20 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-red-500 rounded-full"
                                style={{
                                  width: `${(item.quantity / maxVolume) * 100}%`,
                                }}
                              />
                            </div>
                          </div>
                          <div className="w-16 text-[11px] text-right pr-2">
                            {item.quantity.toFixed(4)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Buy Side (Bids) */}
                  <div className="p-0 m-0 w-full">
                    <div className="space-y-1 w-full">
                      {buys.slice(0, 1).map((item: any, index: number) => (
                        <div key={`buy-depth-${index}`} className="flex items-center space-x-2 cursor-pointer hover:theme-bg-quaternary rounded px-2 py-1 transition-colors duration-200 text-gray-400 theme-text-secondary" onClick={() => handlePriceClick(item.price)}>
                          <div className="w-16 text-[11px]">
                            {formatPriceWithAssetDecimals(item.price, selectedSymbol).replace(/\.(\d{1})$/, '.$10')}
                          </div>
                          <div className="flex-1 relative">
                            <div className="h-2 bg-[#2AB7A6]/20 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-[#2AB7A6] rounded-full"
                                style={{
                                  width: `${(item.quantity / maxVolume) * 100}%`,
                                }}
                              />
                            </div>
                          </div>
                          <div className="w-16 text-[11px] text-right pr-2">
                            {item.quantity.toFixed(4)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* No Data State */}
                  {sells.length === 0 && buys.length === 0 && (
                    <div className="flex-1 flex items-center justify-center">
                      <div className="theme-bg-quaternary rounded-lg p-3 py-2 mx-1 text-gray-400">
                        <div className="text-center text-xs">
                          No market depth data available
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Depth Chart Button */}
                {/* <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button className="w-full py-2 px-3 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium rounded-lg transition-colors duration-200 flex items-center justify-center">
                    <Eye className="h-4 w-4 mr-2" />
                    View Advanced Chart
                  </button>
                </div> */}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketDepth;
